// Per-track instruments (docs/AUDIO_DESIGN.md §3). The EP allocates 3 sines + 3 gains per note (voice cap with
// stealing); drums, bass and lead are persistent oscillators/noise gated with setTargetAtTime, so nothing is
// allocated per hit and a retrigger can't click (setTargetAtTime starts from the current value).
import { clamp } from '../engine/core.js';
import { mtof, hold, noise } from './dsp.js';

// Instrument levels (tuning knobs for the mix balance).
export const LEVELS = { ep: 5.0, bass: 0.5, kit: 1.3, lead: 2.5 };

// Node helpers (the styles' instruments use them too).
export const gain = (ac, v = 1) => { const g = ac.createGain(); g.gain.value = v; return g; };
export const filter = (ac, type, f, Q, dB) => {
  const b = ac.createBiquadFilter(); b.type = type; b.frequency.value = f;
  if (Q != null) b.Q.value = Q;
  if (dB != null) b.gain.value = dB;
  return b;
};
export const osc = (ac, type, f) => { const o = ac.createOscillator(); o.type = type; o.frequency.value = f; return o; };
export const panner = (ac, p) => { const s = ac.createStereoPanner(); s.pan.value = p; return s; };

/** Persistent part of an instrument: registered as one voice, stopped on dispose. Looped buffer sources can start
 *  `offset` seconds in (so two instruments sharing a noise buffer don't play it in step). */
export function persistent(registry, tag, src, all, t0, offset = 0) {
  for (const s of src) { if (offset && s instanceof AudioBufferSourceNode) s.start(t0, offset); else s.start(t0); }
  const v = registry.add({ src, all, start: t0, end: Infinity, tag: `${tag}:p` });
  return {
    dispose(t) {
      for (const s of src) { try { s.stop(t); } catch { /* already stopped */ } }
      v.end = t;                                    // lets sweep() free it if onended never fires
    },
  };
}

/* ---------------- EP: FM electric piano ---------------- */
/** p: { lp, tremRate, tremDepth }. out/send: track dry / wet. opts: { registry, tag, cap, r, t0, log } */
export function createEP(ac, out, send, p, { registry, tag, cap = 16, r = Math.random, t0 = ac.currentTime, log = null }) {
  const busIn = gain(ac, 1), lp = filter(ac, 'lowpass', p.lp, 0.5), trem = gain(ac, 1 - p.tremDepth / 2);
  const pan = panner(ac, 0), sendG = gain(ac, 0.3), lfo = osc(ac, 'sine', p.tremRate);
  const lfoTrem = gain(ac, p.tremDepth / 2), lfoPan = gain(ac, 0.25);
  busIn.connect(lp); lp.connect(trem); trem.connect(pan); pan.connect(out); pan.connect(sendG); sendG.connect(send);
  lfo.connect(lfoTrem); lfoTrem.connect(trem.gain); lfo.connect(lfoPan); lfoPan.connect(pan.pan);
  const bus = persistent(registry, tag, [lfo], [busIn, lp, trem, pan, sendG, lfo, lfoTrem, lfoPan], t0);
  let voices = [];                                  // { v, start, end: sounding end (stolen voices end early) }
  let maxActive = 0, steals = 0, notes = 0;

  function note(t, midi, vel, dur, relTau = 0.08) {
    const f = mtof(midi) * Math.pow(2, (r() * 6 - 3) / 1200);
    voices = voices.filter(x => x.end > t);
    if (voices.length >= cap) {                     // steal the oldest with a 30 ms release
      const old = voices.shift();
      registry.stop(old.v, t, 0.01);
      old.end = t; if (old.rec) old.rec[1] = t;
      steals++;
    }
    const car = osc(ac, 'sine', f), body = osc(ac, 'sine', f), tine = osc(ac, 'sine', 14 * f);
    const bodyG = gain(ac, 0), tineG = gain(ac, 0), amp = gain(ac, 0);
    body.connect(bodyG); bodyG.connect(car.frequency);
    tine.connect(tineG); tineG.connect(car.frequency);
    car.connect(amp); amp.connect(busIn);
    const Ib = (0.8 + 2.2 * vel) * clamp(Math.sqrt(440 / f), 0.5, 1.2);          // body 1:1, decays to 0.25 x peak
    bodyG.gain.setValueAtTime(Ib * f, t); bodyG.gain.setTargetAtTime(0.25 * Ib * f, t, 0.3);
    const It = 0.35 * vel * Math.min(1, Math.pow(440 / f, 0.7));                   // tine 14:1, τ 25 ms
    tineG.gain.setValueAtTime(It * 14 * f, t); tineG.gain.setTargetAtTime(0, t, 0.025);
    const A = 0.16 * Math.pow(vel, 1.4) * LEVELS.ep, off = t + Math.max(0.05, dur);
    amp.gain.setValueAtTime(0, t);
    amp.gain.linearRampToValueAtTime(A, t + 0.003);
    amp.gain.setTargetAtTime(0.55 * A, t + 0.003, 0.09);
    if (off > t + 0.25) amp.gain.setTargetAtTime(0, t + 0.25, 1.4 * Math.pow(262 / f, 0.3));
    amp.gain.setTargetAtTime(0, off, relTau);
    const end = off + Math.max(0.5, relTau * 6);
    for (const o of [car, body, tine]) { o.start(t); o.stop(end); }
    const v = registry.add({ src: [car, body, tine], all: [car, body, tine, bodyG, tineG, amp], start: t, end, tag, out: amp });
    const x = { v, start: t, end: off + relTau * 4, rec: log ? [t, off + relTau * 4] : null };
    if (x.rec) log.push(x.rec);
    voices.push(x);
    notes++;
    maxActive = Math.max(maxActive, voices.length);
  }
  return {
    note,
    /** strummed bottom to top over `strum` seconds; the top voice gets +10 % velocity */
    chord(t, midis, vel, dur, strum = 0.02, relTau = 0.08) {
      const n = midis.length;
      midis.forEach((m, k) => {
        const dt = n > 1 ? (k * strum) / (n - 1) : 0;
        note(t + dt, m, k === n - 1 ? Math.min(1, vel * 1.1) : vel, dur - dt, relTau);
      });
    },
    stats: () => ({ maxActive, steals, notes, active: voices.length }),
    dispose(t) { bus.dispose(t); },
  };
}

/* ---------------- bass: sine + triangle (.35) through a plucked LP ---------------- */
export function createBass(ac, out, { registry, tag, t0 = ac.currentTime }) {
  const sine = osc(ac, 'sine', 55), tri = osc(ac, 'triangle', 55), triG = gain(ac, 0.35);
  const lp = filter(ac, 'lowpass', 700, 0.8), amp = gain(ac, 0);
  sine.connect(lp); tri.connect(triG); triG.connect(lp); lp.connect(amp); amp.connect(out);
  const p = persistent(registry, tag, [sine, tri], [sine, tri, triG, lp, amp], t0);
  return {
    note(t, midi, vel, dur, slide = false) {
      const f = mtof(midi);
      for (const o of [sine, tri]) {
        if (slide) { o.frequency.setValueAtTime(f * Math.pow(2, -100 / 1200), t); o.frequency.setTargetAtTime(f, t, 0.02); }
        else o.frequency.setValueAtTime(f, t);
      }
      lp.frequency.setTargetAtTime(1400, t, 0.003); lp.frequency.setTargetAtTime(500, t + 0.012, 0.12);
      const A = 0.45 * vel * LEVELS.bass;
      amp.gain.setTargetAtTime(A, t, 0.004);
      amp.gain.setTargetAtTime(0.6 * A, t + 0.02, 0.35);
      amp.gain.setTargetAtTime(0, t + Math.max(0.06, dur), 0.04);
    },
    dispose(t) { p.dispose(t); },
  };
}

/* ---------------- drum kit: persistent voices sharing one noise source ---------------- */
/** kit: { kickF0, kickF1, kickDecay, snareDecay, hatHP, hatClosed, lp } */
export function createKit(ac, out, send, kit, { registry, tag, t0 = ac.currentTime }) {
  const L = LEVELS.kit, nodes = [];
  const N = x => (nodes.push(x), x);
  const src = N(ac.createBufferSource());
  src.buffer = noise(ac, 'white'); src.loop = true;
  const bus = N(filter(ac, 'lowpass', kit.lp, 0.5));
  bus.connect(out);
  // kick: sine sweep + noise click
  const kOsc = N(osc(ac, 'sine', kit.kickF1)), kAmp = N(gain(ac, 0));
  kOsc.connect(kAmp); kAmp.connect(bus);
  const kHP = N(filter(ac, 'highpass', 1800, 0.7)), kClick = N(gain(ac, 0));
  src.connect(kHP); kHP.connect(kClick); kClick.connect(bus);
  // snare: triangle body + band-passed noise, reverb send .25
  const sSend = N(gain(ac, 0.25));
  const sOsc = N(osc(ac, 'triangle', 180)), sTone = N(gain(ac, 0));
  sOsc.connect(sTone); sTone.connect(bus); sTone.connect(sSend);
  const sHP = N(filter(ac, 'highpass', 900, 0.7)), sBP = N(filter(ac, 'bandpass', 2400, 0.6)), sNoise = N(gain(ac, 0));
  src.connect(sHP); sHP.connect(sBP); sBP.connect(sNoise); sNoise.connect(bus); sNoise.connect(sSend);
  sSend.connect(send);
  // hat: high-passed noise, pan +.2, send .08 (closed and open share one gain, so closed chokes open)
  const hHP = N(filter(ac, 'highpass', kit.hatHP, 0.7)), hAmp = N(gain(ac, 0)), hPan = N(panner(ac, 0.2)), hSend = N(gain(ac, 0.08));
  src.connect(hHP); hHP.connect(hAmp); hAmp.connect(hPan); hPan.connect(bus); hPan.connect(hSend); hSend.connect(send);
  // rim: sine 470 (.6) + triangle 1650 (.4) -> HP 300, pan -.15, send .3
  const r1 = N(osc(ac, 'sine', 470)), r2 = N(osc(ac, 'triangle', 1650)), rg1 = N(gain(ac, 0.6)), rg2 = N(gain(ac, 0.4));
  const rHP = N(filter(ac, 'highpass', 300, 0.7)), rAmp = N(gain(ac, 0)), rPan = N(panner(ac, -0.15)), rSend = N(gain(ac, 0.3));
  r1.connect(rg1); r2.connect(rg2); rg1.connect(rHP); rg2.connect(rHP); rHP.connect(rAmp); rAmp.connect(rPan);
  rPan.connect(bus); rPan.connect(rSend); rSend.connect(send);
  const p = persistent(registry, tag, [src, kOsc, sOsc, r1, r2], nodes, t0);
  const hit = (param, peak, t, atk, rel, tau) => { param.setTargetAtTime(peak, t, atk); param.setTargetAtTime(0, t + rel, tau); };
  return {
    kick(t, v) {
      kOsc.frequency.setValueAtTime(kit.kickF0, t); kOsc.frequency.setTargetAtTime(kit.kickF1, t, 0.03);
      hit(kAmp.gain, 0.55 * v * L, t, 0.0012, 0.006, kit.kickDecay);
      hit(kClick.gain, 0.12 * v * L, t, 0.0004, 0.0015, 0.004);
    },
    snare(t, v, ghost = false) {
      sOsc.frequency.setValueAtTime(210, t); sOsc.frequency.setTargetAtTime(180, t, 0.03);
      hit(sTone.gain, 0.22 * v * L, t, 0.001, 0.004, 0.04);
      hit(sNoise.gain, 0.4 * v * L, t, 0.001, 0.004, ghost ? 0.035 : kit.snareDecay);
    },
    hat(t, v, open = false) { hit(hAmp.gain, 0.14 * v * L, t, 0.0008, 0.003, open ? 0.12 : kit.hatClosed); },
    rim(t, v) { hit(rAmp.gain, 0.25 * v * L, t, 0.0008, 0.002, 0.007); },
    dispose(t) { p.dispose(t); },
  };
}

/* ---------------- percussion beyond the kit (styles): clap, shaker, tom, block ---------------- */
/** Built by the engine on a track's first percussion hit, one noise source for all. perc (all optional):
 *  { tom: { f0, f1, decay }, block: { f, decay }, clap: { f }, shaker: { hp, decay }, gain: { clap, shaker, tom, block } } */
export function createPerc(ac, out, send, perc = {}, { registry, tag, t0 = ac.currentTime }) {
  const L = LEVELS.kit, nodes = [], G = { clap: 1, shaker: 1, tom: 1, block: 1, ...perc.gain };
  const tom = { f0: 150, f1: 95, decay: 0.16, ...perc.tom }, blk = { f: 820, decay: 0.035, ...perc.block };
  const N = x => (nodes.push(x), x);
  const src = N(ac.createBufferSource());
  src.buffer = noise(ac, 'white'); src.loop = true;
  // clap: noise → BP 1.2k, three bursts 10 ms apart and a 60 ms tail, pan −.1, reverb .3
  const cBP = N(filter(ac, 'bandpass', perc.clap?.f ?? 1200, 0.9)), cAmp = N(gain(ac, 0)), cPan = N(panner(ac, -0.1)), cSend = N(gain(ac, 0.3));
  src.connect(cBP); cBP.connect(cAmp); cAmp.connect(cPan); cPan.connect(out); cPan.connect(cSend); cSend.connect(send);
  // shaker: noise → HP 4.5k → BP 7.5k, pan +.3
  const xHP = N(filter(ac, 'highpass', perc.shaker?.hp ?? 4500, 0.7)), xBP = N(filter(ac, 'bandpass', 7500, 0.8)), xAmp = N(gain(ac, 0)), xPan = N(panner(ac, 0.3));
  src.connect(xHP); xHP.connect(xBP); xBP.connect(xAmp); xAmp.connect(xPan); xPan.connect(out);
  // tom: sine sweeping f0 → f1 (τ 40 ms), pan −.2, reverb .15
  const tOsc = N(osc(ac, 'sine', tom.f1)), tAmp = N(gain(ac, 0)), tPan = N(panner(ac, -0.2)), tSend = N(gain(ac, 0.15));
  tOsc.connect(tAmp); tAmp.connect(tPan); tPan.connect(out); tPan.connect(tSend); tSend.connect(send);
  // block: sines f and 2.76 f, pan +.15, reverb .2
  const b1 = N(osc(ac, 'sine', blk.f)), b2 = N(osc(ac, 'sine', blk.f * 2.76)), bg2 = N(gain(ac, 0.35));
  const bAmp = N(gain(ac, 0)), bPan = N(panner(ac, 0.15)), bSend = N(gain(ac, 0.2));
  b1.connect(bAmp); b2.connect(bg2); bg2.connect(bAmp); bAmp.connect(bPan); bPan.connect(out); bPan.connect(bSend); bSend.connect(send);
  const p = persistent(registry, tag, [src, tOsc, b1, b2], nodes, t0);
  const hit = (param, peak, t, atk, rel, tau) => { param.setTargetAtTime(peak, t, atk); param.setTargetAtTime(0, t + rel, tau); };
  return {
    clap(t, v) {
      const A = 0.32 * v * L * G.clap;
      for (const d of [0, 0.01, 0.02]) hit(cAmp.gain, A * (d ? 0.8 : 1), t + d, 0.0004, 0.002, 0.0025);
      hit(cAmp.gain, 0.7 * A, t + 0.03, 0.0005, 0.002, 0.06);
    },
    shaker(t, v) { hit(xAmp.gain, 0.12 * v * L * G.shaker, t, 0.004, 0.008, perc.shaker?.decay ?? 0.03); },
    tom(t, v) {
      tOsc.frequency.setValueAtTime(tom.f0, t); tOsc.frequency.setTargetAtTime(tom.f1, t, 0.04);
      hit(tAmp.gain, 0.5 * v * L * G.tom, t, 0.001, 0.005, tom.decay);
    },
    block(t, v) { hit(bAmp.gain, 0.22 * v * L * G.block, t, 0.0005, 0.001, blk.decay); },
    dispose(t) { p.dispose(t); },
  };
}

/* ---------------- lead: "soft" (sine + triangle 2f) or "vibes" (FM 1:4), mono, dotted-8th delay ---------------- */
/** lead: { timbre, pan }. stepDur sets the dotted-8th delay (3 sixteenths). */
export function createLead(ac, out, send, lead, { registry, tag, stepDur, t0 = ac.currentTime }) {
  const nodes = [], src = [];
  const N = x => (nodes.push(x), x);
  const amp = N(gain(ac, 0)), pan = N(panner(ac, lead.pan || 0)), sendG = N(gain(ac, 0.4));
  const dly = N(ac.createDelay(2)), dLP = N(filter(ac, 'lowpass', 2500, 0.5)), fb = N(gain(ac, 0.3)), wet = N(gain(ac, 0.35));
  dly.delayTime.value = Math.min(1.9, 3 * stepDur);
  amp.connect(pan); amp.connect(dly); dly.connect(dLP); dLP.connect(fb); fb.connect(dly); dLP.connect(wet); wet.connect(pan);
  pan.connect(out); pan.connect(sendG); sendG.connect(send);
  const L = LEVELS.lead;
  let play;
  if (lead.timbre === 'vibes') {
    const car = N(osc(ac, 'sine', 440)), mod = N(osc(ac, 'sine', 1760)), modG = N(gain(ac, 0));
    mod.connect(modG); modG.connect(car.frequency); car.connect(amp);
    src.push(car, mod);
    play = (t, f, vel, dur) => {
      car.frequency.setValueAtTime(f, t); mod.frequency.setValueAtTime(4 * f, t);
      modG.gain.setTargetAtTime(1.5 * 4 * f, t, 0.002); modG.gain.setTargetAtTime(0.1 * 4 * f, t + 0.01, 0.2);
      amp.gain.setTargetAtTime(0.16 * vel * L, t, 0.002); amp.gain.setTargetAtTime(0, t + 0.01, 1.2);
      amp.gain.setTargetAtTime(0, t + dur, 0.25);
    };
  } else {
    const o1 = N(osc(ac, 'sine', 440)), o2 = N(osc(ac, 'triangle', 880)), g2 = N(gain(ac, 0.15));
    const lp = N(filter(ac, 'lowpass', 2500, 0.5)), vib = N(osc(ac, 'sine', 5)), vibG = N(gain(ac, 0));
    o1.connect(lp); o2.connect(g2); g2.connect(lp); lp.connect(amp);
    vib.connect(vibG); vibG.connect(o1.detune); vibG.connect(o2.detune);
    src.push(o1, o2, vib);
    play = (t, f, vel, dur, glide) => {
      if (glide) { o1.frequency.setTargetAtTime(f, t, 0.013); o2.frequency.setTargetAtTime(2 * f, t, 0.013); }
      else { o1.frequency.setValueAtTime(f, t); o2.frequency.setValueAtTime(2 * f, t); }
      amp.gain.setTargetAtTime(0.2 * vel * L, t, 0.007);
      amp.gain.setTargetAtTime(0, t + dur, 0.08);
      vibG.gain.setTargetAtTime(0, t, 0.02);
      if (dur > 0.3) vibG.gain.setTargetAtTime(10, t + 0.25, 0.12);
    };
  }
  const p = persistent(registry, tag, src, nodes, t0);
  return {
    note(t, midi, vel, dur, glide = false) { play(t, mtof(midi), vel, Math.max(0.06, dur), glide); },
    dispose(t) { p.dispose(t); },
  };
}

/** Fade a track bus out quickly (user skip) — exported for the engine. */
export function fadeOut(param, t, tau = 0.1) { hold(param, t); param.setTargetAtTime(0, t, tau); }
